import { CSSProperties, FC, ReactNode, memo } from 'react';
// 组件
import { ProDropdown, ProButton, ProTooltip, } from '../antd';
// 常量
import { IS_MOBILE } from '../constants/constant';
import { MenuProps } from 'antd';

export interface IBtnList {
  key: string;
  label?: ReactNode;
  icon?: ReactNode;
  hoverColor?: string;
  disabled?: boolean;
  danger?: boolean;
  render?: ReactNode;
}
export interface IlineList {
  color?: string;
  lists: IBtnList[];
}
export interface ButtonGroupProps {
  /** 按钮列表 */
  btnLists: IBtnList[];
  /** 样式 */
  style?: CSSProperties;
  /** 按钮底部额外配置 */
  footerList?: IBtnList[];
  /** 按钮和额外配置之间分割线颜色 */
  footerBorderColor?: string;
  /** 底部分割线按钮组 */
  lineLists?: IlineList[];
  /** 是否显示标题 */
  showLabel?: boolean;
  /** 是否在表格的操作列且居中使用 */
  isTableOperation?: boolean;
  /** 点击按钮组 */
  onClick?: (key: string) => void;
}
/**
 * 表格操作栏按钮组
 */
export const ButtonGroup: FC<ButtonGroupProps> = memo(props => {
  const {
    style,
    btnLists,
    footerList,
    showLabel,
    footerBorderColor,
    isTableOperation,
    lineLists = [],
    onClick,
  } = props;
  if (!btnLists) return null;
  if (btnLists.length === 0) return null;
  const firstBtn = btnLists[0];
  const handleMenuClick = (key: string) => {
    onClick && onClick(key);
  };
  const lineItems: IlineList[] = [...lineLists].filter(item => !!item.lists.length);
  if (Array.isArray(footerList) && footerList.length) {
    lineItems.unshift({
      color: footerBorderColor,
      lists: footerList,
    });
  }
  if (btnLists.length === 1 && lineItems.length === 0) {
    const operationStyle: CSSProperties | undefined = isTableOperation ? { marginRight: 23, } : undefined;
    if (firstBtn.render) {
      return (
        <div
          style={operationStyle}
          onClick={e => {
            e.stopPropagation?.();
            !firstBtn.disabled && handleMenuClick(firstBtn.key);
          }}
        >{firstBtn.icon}{firstBtn.render}</div>
      )
    }
    return (
      <ProButton
        style={operationStyle}
        onClick={e => {
          e.stopPropagation?.();
          handleMenuClick(firstBtn.key);
        }}
        test-id={`button-group-${firstBtn.key}`}
        danger={firstBtn.danger}
      >
        {firstBtn.icon}
        {firstBtn.label}
      </ProButton>
    );
  }
  const items = (): MenuProps => {
    const newItems: MenuProps['items'] = [];
    btnLists.slice(1).forEach(item => {
      const { key, icon, danger, disabled, render, label, } = item;
      newItems.push({
        key,
        icon,
        danger,
        disabled,
        label: render || label,
      });
    });
    lineItems.forEach(group => {
      group.lists.forEach((item, index) => {
        const { key, icon, danger, disabled, render, label, } = item;
        newItems.push({
          key,
          icon,
          danger,
          disabled,
          style: (index === 0 && newItems.length) ? { borderTop: `solid 1px ${group.color || footerBorderColor}`, borderTopLeftRadius: 0, borderTopRightRadius: 0, } : undefined,
          label: render || label,
        });
      });
    });
    return {
      items: newItems,
      onClick: e => {
        e.domEvent?.stopPropagation?.();
        handleMenuClick(e.key as string);
      },
    };
  };
  return (
    <ProDropdown.Button
      style={{ width: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: isTableOperation ? 'center' : 'flex-start'}}
      menu={items()}
      onClick={e => {
        e.stopPropagation?.();
        handleMenuClick(firstBtn.key);
      }}
      test-id="button-group"
      /** 适配移动端 */
      trigger={IS_MOBILE ? ['click'] : ['hover']}
    >
      {
        showLabel ? (
          <>
            {firstBtn.icon}
            <span className='p-l-s'>{firstBtn.label}</span>
          </>
        ) : (
          <ProTooltip title={firstBtn.label}>
            {firstBtn.icon}
          </ProTooltip>
        )
      }
    </ProDropdown.Button>
  );
});

ButtonGroup.defaultProps = {
  footerBorderColor: '#1890ff',
  showLabel: true,
  isTableOperation: true,
};
export default ButtonGroup;
